/**
 * Storage Utility - Prioritizes localStorage over cookies for iframe compatibility
 *
 * localStorage works better in iframes and mobile webviews:
 * - No SameSite restrictions
 * - No third-party cookie blocking
 * - Each origin gets isolated storage (secure)
 * - Works in most mobile webviews
 */

import { Cookie } from "~/types";

/**
 * Set value in localStorage (primary) and cookie (fallback for backwards compat)
 */
export function setStorage(key: Cookie | string, value: string): void {
  try {
    // Primary: Use localStorage
    localStorage.setItem(key, value);
  } catch (e) {
    console.warn(`Failed to set localStorage for ${key}:`, e);
  }

  // Fallback: Also set cookie for backwards compatibility
  // Some older code might still read from cookies
  try {
    setCookieFallback(key, value);
  } catch (e) {
    console.warn(`Failed to set cookie fallback for ${key}:`, e);
  }
}

/**
 * Get value from localStorage (primary) or cookie (fallback)
 */
export function getStorage(key: Cookie | string): string | null {
  try {
    // Primary: Try localStorage first
    const value = localStorage.getItem(key);
    if (value !== null) {
      return value;
    }
  } catch (e) {
    console.warn(`Failed to read localStorage for ${key}:`, e);
  }

  // Fallback: Try cookie
  try {
    return getCookieFallback(key);
  } catch (e) {
    console.warn(`Failed to read cookie fallback for ${key}:`, e);
  }

  return null;
}

/**
 * Remove value from both localStorage and cookie
 */
export function removeStorage(key: Cookie | string): void {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn(`Failed to remove localStorage for ${key}:`, e);
  }

  try {
    clearCookieFallback(key);
  } catch (e) {
    console.warn(`Failed to clear cookie fallback for ${key}:`, e);
  }
}

/**
 * Check if value exists in storage
 */
export function hasStorage(key: Cookie | string): boolean {
  return getStorage(key) !== null;
}

/**
 * Clear all app storage (useful for logout)
 */
export function clearAllStorage(): void {
  const keys = [
    Cookie.SessionId,
    Cookie.ClientId,
    Cookie.ClientSecret,
    Cookie.Mode,
    Cookie.MockRepo,
    Cookie.RnrPortalsJson,
    "isDarkTheme",
    "rnrPortalsJson",
  ];

  keys.forEach((key) => removeStorage(key));
}

// ============================================================================
// Cookie Fallback Functions (for backwards compatibility)
// ============================================================================

function setCookieFallback(key: string, value: string): void {
  const expiryDays = 365;
  const expiryTime = new Date().getTime() + expiryDays * 24 * 60 * 60 * 1000;
  const d = new Date(expiryTime);
  const expires = "expires=" + d.toUTCString();

  // Simple cookie without SameSite restrictions
  // Since we're primarily using localStorage, cookies are just fallback
  const cookieString = `${key}=${value}; ${expires}; path=/`;
  document.cookie = cookieString;
}

function getCookieFallback(key: string): string | null {
  const name = key + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return null;
}

function clearCookieFallback(key: string): void {
  document.cookie = `${key}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT`;
}

/**
 * Get storage info for debugging
 */
export function getStorageDebugInfo(): {
  localStorage: boolean;
  cookiesFallback: boolean;
  keys: string[];
} {
  const keys: string[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) keys.push(key);
    }
  } catch (e) {
    // localStorage not available
  }

  return {
    localStorage: typeof localStorage !== "undefined",
    cookiesFallback: typeof document !== "undefined" && typeof document.cookie !== "undefined",
    keys,
  };
}
